import React, { useEffect, useState, useContext } from 'react';
import './Sidebar.css';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { RefreshContext } from '../../../App';
import {
    IoChevronBack, IoChevronForward, IoGridOutline, IoBriefcaseOutline,
    IoPeopleOutline, IoSettingsOutline, IoLogOutOutline, IoConstructOutline,
    IoChevronDown, IoLayersOutline, IoDocumentTextOutline, IoBuildOutline
} from 'react-icons/io5';
import { BsPersonGear } from 'react-icons/bs';
import { HiOutlineUsers, HiOutlineUserGroup } from 'react-icons/hi';
import { MdOutlineChecklist, MdWorkOutline } from "react-icons/md";
import { hasPermission } from "../../../utils/permission";

// --- Environment Variables ---
const ZTASK_TOOLS_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const ZTOOLS_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR = process.env.REACT_APP_ZTOOL_URL;
const GET_COMPANY_DETAILS_ENDPOINT = `${ZTOOLS_BASE_URL}/api/get-company`;
const GET_USERS_ENDPOINT = `${ZTOOLS_BASE_URL}/api/get-users`;

const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';

// --- Skeleton and Loader Components ---
const SidebarSkeleton = ({ isCollapsed }) => (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header"><div className={`skeleton-block ${isCollapsed ? 'logo-skeleton-collapsed' : 'logo-skeleton-expanded'}`}></div></div>
        <div className="sidebar-content"><div className="menu-group">{!isCollapsed && <div className="skeleton-line menu-title-skeleton"></div>}<ul>{Array.from({ length: 5 }).map((_, index) => (<li key={index} className="skeleton-menu-item"><div className="skeleton-block icon-skeleton-sidebar"></div>{!isCollapsed && <div className="skeleton-line text-skeleton-sidebar"></div>}</li>))}</ul></div></div>
        <div className="sidebar-footer"><div className="skeleton-user-profile"><div className="skeleton-block avatar-skeleton"></div>{!isCollapsed && (<div className="skeleton-user-info"><div className="skeleton-line user-name-skeleton"></div><div className="skeleton-line user-subtext-skeleton"></div></div>)}</div></div>
    </div>
);

const ProfileInfoLoader = () => (
    <div className="user-additional-info">
        <div className="info-item"><span className="info-label">Role:</span><span className="info-value info-loader">Loading...</span></div>
        <div className="info-item"><span className="info-label">Designation:</span><span className="info-value info-loader">Loading...</span></div>
        <div className="info-item"><span className="info-label">Department:</span><span className="info-value info-loader">Loading...</span></div>
    </div>
);


const Sidebar = ({ refreshKey }) => {
    const getInitialCollapsedState = () => localStorage.getItem('sidebarCollapsed') === 'true';
    const location = useLocation();
    
    const { triggerRefresh } = useContext(RefreshContext);

    const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState);
    const [userDetails, setUserDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [companyName, setCompanyName] = useState('Loading...');
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [companyWebsiteUrl, setCompanyWebsiteUrl] = useState('#');
    const [companyKeyword, setCompanyKeyword] = useState(null);
    const [currentUserData, setCurrentUserData] = useState(null);

    const toggleProfileDropdown = () => setIsProfileOpen(prevState => !prevState);

    useEffect(() => {
        const handleProfileUpdate = () => {
            const storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
            if (storedUserDetailsString) {
                try {
                    setUserDetails(JSON.parse(storedUserDetailsString));
                } catch(e) { console.error("Sidebar: Failed to parse user details.", e); }
            }
        };
        window.addEventListener('profileUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
    }, []);

    useEffect(() => {
        const loadSidebarData = async () => {
            if (!userDetails) setIsLoading(true); else setIsUpdatingProfile(true);
            const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
            let storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
            if (userDetails) { storedUserDetailsString = JSON.stringify(userDetails); }
            if (!storedToken || !storedUserDetailsString) { if (ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR) window.location.replace(ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR); return; }
            let parsedUserDetails;
            try { parsedUserDetails = JSON.parse(storedUserDetailsString); if (!userDetails) setUserDetails(parsedUserDetails); } catch (e) { console.error("Sidebar: Failed to parse userDetails.", e); localStorage.clear(); if (ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR) window.location.replace(ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR); return; }
            try {
                const [companyDetailsResponse, usersResponse] = await Promise.all([ fetch(GET_COMPANY_DETAILS_ENDPOINT, { headers: { 'Authorization': `Bearer ${storedToken}` } }), fetch(GET_USERS_ENDPOINT, { headers: { 'Authorization': `Bearer ${storedToken}` } }) ]);
                if (companyDetailsResponse.ok) { 
                    const companyData = await companyDetailsResponse.json(); 
                    const companyList = companyData.message || companyData.messgae; 
                    if (Array.isArray(companyList) && companyList.length > 0) { 
                        const companyInfo = companyList[0]; 
                        setCompanyName(companyInfo.companyName || 'Company'); 
                        setCompanyLogoUrl(companyInfo.logoUrl); 
                        setCompanyKeyword(companyInfo.companyKeyword);

                        const rawUrl = companyInfo.websiteUrl;
                        if (rawUrl && typeof rawUrl === 'string') {
                            if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
                                setCompanyWebsiteUrl(rawUrl);
                            } else {
                                setCompanyWebsiteUrl(`https://${rawUrl}`);
                            }
                        } else {
                            setCompanyWebsiteUrl('#');
                        }
                    } else { 
                        setCompanyName('My App'); 
                    } 
                } else { 
                    setCompanyName('My App'); 
                }
                let role = 'N/A', designation = 'N/A', department = 'N/A';
                if (usersResponse.ok) { const usersData = await usersResponse.json(); if (Array.isArray(usersData.userData)) { const currentUser = usersData.userData.find(user => user.email === parsedUserDetails.email); if (currentUser) { role = currentUser.role?.name || 'N/A'; designation = currentUser.designation?.designationName || 'N/A'; department = currentUser.department?.departmentName || 'N/A'; } } }
                setCurrentUserData({ role: role.toUpperCase(), designation: designation.toUpperCase(), department: department.toUpperCase() });
            } catch (error) { console.error("Error fetching sidebar data:", error); setCurrentUserData({ role: (userDetails?.role?.name || 'N/A').toUpperCase(), designation: (userDetails?.designation?.designationName || 'N/A').toUpperCase(), department: (userDetails?.department?.departmentName || 'N/A').toUpperCase() }); } finally { setIsLoading(false); setIsUpdatingProfile(false); }
        };
        loadSidebarData();
    }, [refreshKey, userDetails]);

    const toggleSidebarCollapse = () => {
        setIsCollapsed(prevState => {
            const newState = !prevState;
            localStorage.setItem('sidebarCollapsed', newState);
            return newState;
        });
    };

    const handleNavLinkClick = (e, path) => {
        if (location.pathname === path) {
            e.preventDefault();
            triggerRefresh();
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        if (ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR) {
            window.location.replace(ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR);
        }
    };
    
    const LogoContent = () => (<div className="logo">{companyLogoUrl ? (<img src={companyLogoUrl} alt="Company Logo" className="logo-image" />) : (<span className="logo-initials">{companyName === 'Loading...' ? '' : (companyName || 'A').charAt(0).toUpperCase()}</span>)}{!isCollapsed && <span className="logo-name-text">{companyName}</span>}</div>);
    
    if (isLoading) return <SidebarSkeleton isCollapsed={isCollapsed} />;
    if (!userDetails) return null;
    
    const userRole = currentUserData?.role;
    const isSuperAdmin = userRole === 'SUPER_ADMIN';
    const avatarInitial = userDetails.name ? userDetails.name.charAt(0).toUpperCase() : 'U';
    
    const menuItems = [
        { id: 'dashboard', icon: <IoGridOutline />, text: 'Dashboard', path: '/dashboard' },
        { id: 'projects', icon: <IoBriefcaseOutline />, text: 'Projects', path: '/Project' },
    ];

    if (hasPermission("work-order", "view")) {
        menuItems.push({ id: 'work-orders', icon: <MdOutlineChecklist />, text: 'Work Orders', path: '/work-orders' });
    }
    if (hasPermission("invoice", "view")) {
        menuItems.push({ id: 'invoices', icon: <IoDocumentTextOutline />, text: 'Invoices', path: '/invoices' });
    }
    if (isSuperAdmin) {
        menuItems.push({ id: 'assets', icon: <IoConstructOutline />, text: 'Assets', path: '/assets' });
    }
    if (isSuperAdmin || userRole === 'USER' || userRole === 'REPORTING MANAGER') {
        menuItems.push({ id: 'customers', icon: <HiOutlineUserGroup />, text: 'Customers', path: '/customers' });
    }
    
    const organizationItems = [];
    if (isSuperAdmin) {
        organizationItems.push({ id: 'team', icon: <IoPeopleOutline />, text: 'Team', path: '/organization/team' });
        organizationItems.push({ id: 'adminManager', icon: <BsPersonGear />, text: 'User Management', path: '/organization/user-management'});

        // --- MODIFIED SECTION ---
        // Conditionally add items based on companyKeyword and user's email
        const userEmail = userDetails?.email || '';
        if (companyKeyword !== 'PL' || userEmail.endsWith('@zasyaonline.com')) {
            organizationItems.push({ id: 'commonMaster', icon: <IoLayersOutline />, text: 'Common Master', path: '/organization/common-master'});
            organizationItems.push({ id: 'workType', icon: <MdWorkOutline />, text: 'Work Type', path: '/organization/work-types'});
        }
    }
    organizationItems.push({ id: 'teamMgmt', icon: <HiOutlineUsers />, text: 'Team Management', path: '/organization/team-management' });

    const reportsItems = [
        { id: 'reports', icon: <IoDocumentTextOutline />, text: 'Reports', path: '/reports' }
    ];

    // --- MODIFIED SECTION ---
    // Conditionally add "Report Setting" based on the same logic
    if (isSuperAdmin) {
        const userEmail = userDetails?.email || '';
        if (companyKeyword !== 'PL' || userEmail.endsWith('@zasyaonline.com')) {
            reportsItems.push({ id: 'reportSettings', icon: <IoBuildOutline />, text: 'Report Setting', path: '/reports/settings' });
        }
    }

    const userSectionItems = [
        { id: 'settingsUser', icon: <IoSettingsOutline />, text: 'Settings', path: '/settings' },
    ];

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
             <div className="sidebar-header">
                <a href={companyWebsiteUrl} className="logo-link" title={`${companyName} Home`} target="_blank" rel="noopener noreferrer"><LogoContent /></a>
                <div onClick={toggleSidebarCollapse} className="collapse-icon-wrapper" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSidebarCollapse(); }} aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"} title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
                    {isCollapsed ? <IoChevronForward className="collapse-icon" /> : <IoChevronBack className="collapse-icon" />}
                </div>
            </div>
            <div className="sidebar-content">
                <div className="menu-group">{!isCollapsed && <p className="menu-group-title">MENU</p>}<ul>{menuItems.map(item => (<li key={item.id} title={isCollapsed ? item.text : undefined}><NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'selected' : ''}`} onClick={(e) => handleNavLinkClick(e, item.path)}><span className="menu-item-icon">{item.icon}</span>{!isCollapsed && <span className="menu-item-text">{item.text}</span>}</NavLink></li>))}</ul></div>
                {organizationItems.length > 0 && (<div className="menu-group">{!isCollapsed && <p className="menu-group-title">ORGANIZATION</p>}<ul>{organizationItems.map(item => (<li key={item.id} title={isCollapsed ? item.text : undefined}><NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'selected' : ''}`} onClick={(e) => handleNavLinkClick(e, item.path)}><span className="menu-item-icon">{item.icon}</span>{!isCollapsed && <span className="menu-item-text">{item.text}</span>}</NavLink></li>))}</ul></div>)}
                
                {reportsItems.length > 0 && (
                    <div className="menu-group">
                        {!isCollapsed && <p className="menu-group-title">REPORTS</p>}
                        <ul>
                            {reportsItems.map(item => (
                                <li key={item.id} title={isCollapsed ? item.text : undefined}>
                                    <NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'selected' : ''}`} onClick={(e) => handleNavLinkClick(e, item.path)}>
                                        <span className="menu-item-icon">{item.icon}</span>
                                        {!isCollapsed && <span className="menu-item-text">{item.text}</span>}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {userSectionItems.length > 0 && (<div className="menu-group">{!isCollapsed && <p className="menu-group-title">USER</p>}<ul>{userSectionItems.map(item => (<li key={item.id} title={isCollapsed ? item.text : undefined}><NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'selected' : ''}`} onClick={(e) => handleNavLinkClick(e, item.path)}><span className="menu-item-icon">{item.icon}</span>{!isCollapsed && <span className="menu-item-text">{item.text}</span>}</NavLink></li>))}</ul></div>)}
            </div>
            <div className="sidebar-footer">
                <div className="footer-separator"></div>
                <div className="user-profile-wrapper">{!isCollapsed ? (<div className="user-profile-section"><Link to="/settings/profile" className="avatar-link" title="View Profile"><div className="avatar">{avatarInitial}</div></Link><div className="user-info"><div className="user-info-header" onClick={toggleProfileDropdown} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleProfileDropdown(); }}><span className="user-name">{userDetails.name || 'User'}</span><IoChevronDown className={`profile-dropdown-icon ${isProfileOpen ? 'open' : ''}`} /></div><Link to="/settings/profile" className="user-subtext-link"><span className="user-subtext">Profile • Settings</span></Link><div className={`user-details-container ${isProfileOpen ? 'open' : ''}`}>{isUpdatingProfile ? (<ProfileInfoLoader />) : (currentUserData && (<div className="user-additional-info"><div className="info-item"><span className="info-label">Role:</span><span className="info-value">{currentUserData.role}</span></div><div className="info-item"><span className="info-label">Designation:</span><span className="info-value">{currentUserData.designation}</span></div><div className="info-item"><span className="info-label">Department:</span><span className="info-value">{currentUserData.department}</span></div></div>))}</div></div></div>) : (<Link to="/settings/profile" className="user-profile-link" title={`${userDetails.name} - Profile & Settings`}><div className="user-profile-section collapsed-user-profile"><div className="avatar">{avatarInitial}</div></div></Link>)}</div>
                <div onClick={handleLogout} className="logout-section menu-item" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLogout(); }} title={isCollapsed ? "Logout" : "Logout"}><span className="menu-item-icon"><IoLogOutOutline /></span>{!isCollapsed && <span className="menu-item-text">Logout</span>}</div>
            </div>
        </div>
    );
};

export default Sidebar;